import {
  FieldRoot,
  FieldLabel,
  FieldHelperText,
  FieldErrorText,
  type FieldRootProps,
} from '@chakra-ui/react';
import { forwardRef, type ReactNode } from 'react';

export interface FieldProps extends Omit<FieldRootProps, 'label'> {
  label?: ReactNode;
  helperText?: ReactNode;
  errorText?: ReactNode;
  optionalText?: ReactNode;
  /**
   * Always render a fixed-height slot for helper/error text so the layout
   * does not jump when a message appears or disappears.
   */
  reserveMessageSpace?: boolean;
}

export const Field = forwardRef<HTMLDivElement, FieldProps>(
  function Field(props, ref) {
    const {
      label,
      children,
      helperText,
      errorText,
      optionalText,
      reserveMessageSpace,
      ...rest
    } = props;
    const messageStyle = { display: 'block', minHeight: 33 } as const;
    const messageSlot = (
      <div
        style={{ minHeight: 33 }}
        data-testid="field-message-slot"
      >
        {helperText && <FieldHelperText style={messageStyle}>{helperText}</FieldHelperText>}
        {errorText && (
          <FieldErrorText
            color="red.300"
            style={{ ...messageStyle, fontWeight: 400 }}
          >
            {errorText}
          </FieldErrorText>
        )}
      </div>
    );
    return (
      <FieldRoot ref={ref} {...rest}>
        {label && (
          <FieldLabel>
            {label}
            {optionalText && (
              <span
                style={{ fontSize: '0.8em', color: 'gray', marginLeft: '4px' }}
              >
                {optionalText}
              </span>
            )}
          </FieldLabel>
        )}
        {children}
        {reserveMessageSpace ? (
          messageSlot
        ) : (
          <>
            {helperText && (
              <FieldHelperText style={messageStyle}>{helperText}</FieldHelperText>
            )}
            {errorText && (
              <FieldErrorText
                color="red.300"
                style={{ ...messageStyle, fontWeight: 400 }}
              >
                {errorText}
              </FieldErrorText>
            )}
          </>
        )}
      </FieldRoot>
    );
  },
);
